/**
 * Browser softphone transport using @twilio/voice-sdk (WebRTC, Opus preferred).
 * REST-dialed client invites do not carry the parent CallSid; expectCall arms the next invite for that join.
 */
import { Call, Device } from '@twilio/voice-sdk';

const OPUS = (Call && Call.Codec && Call.Codec.Opus) || 'opus';
const PCMU = (Call && Call.Codec && Call.Codec.PCMU) || 'pcmu';

function clamp01(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function emptyDevices() {
  return { inputs: [], outputs: [], inputId: null, outputId: null };
}

function tokenFrom(result) {
  if (typeof result === 'string') return result;
  if (result && typeof result.token === 'string') return result.token;
  return '';
}

function errorText(err, fallback) {
  if (err && typeof err.message === 'string' && err.message) return err.message;
  return fallback;
}

function mapDevices(deviceMap) {
  const out = [];
  if (!deviceMap || typeof deviceMap.forEach !== 'function') return out;
  deviceMap.forEach((info, id) => {
    const deviceId = info && info.deviceId ? String(info.deviceId) : String(id);
    out.push({
      id: deviceId,
      label: info && info.label ? String(info.label) : deviceId
    });
  });
  return out;
}

function carriedJoinId(incoming) {
  const custom = incoming && incoming.customParameters;
  if (!custom || typeof custom.get !== 'function') return '';
  const value = custom.get('callSid') || custom.get('parentCallSid') || custom.get('conference') || '';
  return value ? String(value) : '';
}

export function createVoiceDevice({ fetchToken }) {
  let device = null;
  let call = null;
  let pendingIncoming = null;
  let expectedCallSid = '';
  let dropUnarmed = false;
  let desiredMuted = true;
  let destroyed = false;
  let levelTimer = null;
  const listeners = [];
  let state = {
    status: 'idle',
    muted: false,
    startedAt: null,
    inputLevel: 0,
    outputLevel: 0,
    error: null,
    devices: emptyDevices()
  };

  function emit() {
    const snapshot = state;
    listeners.forEach((fn) => {
      try { fn(snapshot); } catch (err) { console.error(err); }
    });
  }

  function patch(partial) {
    state = Object.assign({}, state, partial);
    emit();
  }

  function noteLevels(inputVolume, outputVolume) {
    state = Object.assign({}, state, {
      inputLevel: clamp01(inputVolume),
      outputLevel: clamp01(outputVolume)
    });
    if (levelTimer) return;
    levelTimer = setTimeout(() => {
      levelTimer = null;
      emit();
    }, 100);
  }

  function refreshDeviceLists(preferInput, preferOutput) {
    const audio = device && device.audio;
    const inputs = mapDevices(audio && audio.availableInputDevices);
    const outputs = mapDevices(audio && audio.availableOutputDevices);
    let inputId = preferInput || null;
    let outputId = preferOutput || null;
    if (!inputId && audio && audio.inputDevice) inputId = audio.inputDevice.deviceId || null;
    if (!outputId && audio && audio.speakerDevices && typeof audio.speakerDevices.get === 'function') {
      const active = audio.speakerDevices.get();
      if (active && typeof active.forEach === 'function') {
        active.forEach((info) => {
          if (!outputId && info) outputId = info.deviceId || null;
        });
      }
    }
    patch({ devices: { inputs, outputs, inputId, outputId } });
  }

  function finishCall(bound) {
    if (call !== bound) return;
    call = null;
    if (pendingIncoming === bound) pendingIncoming = null;
    patch({ status: 'ended', inputLevel: 0, outputLevel: 0 });
  }

  function bindCall(incoming) {
    call = incoming;
    const bound = incoming;
    incoming.on('accept', () => {
      if (call !== bound) return;
      try { bound.mute(desiredMuted); } catch (_) { /* media not up yet */ }
      patch({
        status: 'connected',
        muted: desiredMuted,
        startedAt: state.startedAt || Date.now(),
        error: null
      });
    });
    incoming.on('disconnect', () => finishCall(bound));
    incoming.on('cancel', () => finishCall(bound));
    incoming.on('reject', () => finishCall(bound));
    incoming.on('error', (err) => {
      if (call !== bound) return;
      patch({ status: 'error', error: errorText(err, 'voice_call_error') });
    });
    incoming.on('mute', (isMuted) => {
      if (call !== bound) return;
      desiredMuted = !!isMuted;
      patch({ muted: desiredMuted });
    });
    incoming.on('volume', (inputVolume, outputVolume) => {
      if (call !== bound) return;
      noteLevels(inputVolume, outputVolume);
    });
  }

  function onIncoming(incoming) {
    const carried = carriedJoinId(incoming);
    const armed = !!expectedCallSid;
    const matches = !carried || carried === expectedCallSid;
    if (armed && matches) {
      expectedCallSid = '';
      dropUnarmed = false;
      pendingIncoming = null;
      bindCall(incoming);
      try {
        incoming.accept();
        incoming.mute(desiredMuted);
      } catch (err) {
        patch({ status: 'error', error: errorText(err, 'voice_accept_failed') });
      }
      return;
    }
    if (!armed && dropUnarmed) {
      try { incoming.reject(); } catch (_) { /* invite already gone */ }
      return;
    }
    pendingIncoming = incoming;
    bindCall(incoming);
    if (state.status !== 'connected') patch({ status: 'connecting' });
  }

  async function refreshAccessToken() {
    if (destroyed || !device) return;
    const next = tokenFrom(await fetchToken());
    if (!next || destroyed || !device) return;
    device.updateToken(next);
  }

  async function ensureDevice() {
    if (device) return device;
    const token = tokenFrom(await fetchToken());
    if (!token) throw new Error('voice_token_missing');
    const created = new Device(token, {
      codecPreferences: [OPUS, PCMU],
      edge: 'roaming'
    });
    device = created;
    created.on('registered', () => {
      if (destroyed || device !== created) return;
      refreshDeviceLists();
      if (call || expectedCallSid || state.status === 'connected' || state.status === 'connecting') return;
      patch({ status: 'ready', error: null });
    });
    created.on('unregistered', () => {
      if (destroyed || device !== created || call) return;
      patch({ status: 'idle' });
    });
    created.on('error', (err) => {
      if (destroyed || device !== created) return;
      const message = errorText(err, 'voice_device_error');
      if (call) patch({ error: message });
      else patch({ status: 'error', error: message });
    });
    created.on('incoming', onIncoming);
    created.on('tokenWillExpire', () => {
      refreshAccessToken().catch((err) => {
        if (!destroyed) patch({ error: errorText(err, 'voice_token_refresh_failed') });
      });
    });
    if (created.audio && typeof created.audio.on === 'function') {
      created.audio.on('deviceChange', () => {
        if (!destroyed && device === created) refreshDeviceLists();
      });
    }
    return created;
  }

  async function register() {
    if (destroyed) throw new Error('voice_device_destroyed');
    if (state.status !== 'connected') patch({ status: 'registering', error: null });
    try {
      const current = await ensureDevice();
      if (current.state === 'registered') {
        if (!call && !expectedCallSid && state.status !== 'connected' && state.status !== 'connecting') {
          patch({ status: 'ready', error: null });
        }
        refreshDeviceLists();
        return;
      }
      await current.register();
      refreshDeviceLists();
    } catch (err) {
      patch({ status: 'error', error: errorText(err, 'voice_register_failed') });
      throw err;
    }
  }

  function destroy() {
    destroyed = true;
    if (levelTimer) {
      clearTimeout(levelTimer);
      levelTimer = null;
    }
    expectedCallSid = '';
    const pending = pendingIncoming;
    pendingIncoming = null;
    const current = call;
    call = null;
    if (pending && pending !== current) {
      try { pending.reject(); } catch (_) { /* already gone */ }
    }
    if (current) {
      try { current.disconnect(); } catch (_) { /* already gone */ }
    }
    if (device) {
      try { device.destroy(); } catch (_) { /* already destroyed */ }
      device = null;
    }
    patch({
      status: 'idle',
      muted: false,
      startedAt: null,
      inputLevel: 0,
      outputLevel: 0,
      error: null,
      devices: emptyDevices()
    });
  }

  function expectCall(callSid) {
    expectedCallSid = callSid == null ? '' : String(callSid);
    dropUnarmed = false;
    desiredMuted = true;
    if (!expectedCallSid) return;
    patch({
      status: 'connecting',
      muted: true,
      startedAt: null,
      inputLevel: 0,
      outputLevel: 0,
      error: null
    });
  }

  function accept() {
    if (state.status === 'connected') return;
    const incoming = pendingIncoming || call;
    if (!incoming || typeof incoming.accept !== 'function') return;
    pendingIncoming = null;
    expectedCallSid = '';
    dropUnarmed = false;
    try {
      incoming.accept();
      incoming.mute(desiredMuted);
    } catch (err) {
      patch({ status: 'error', error: errorText(err, 'voice_accept_failed') });
    }
  }

  function hangup() {
    dropUnarmed = true;
    expectedCallSid = '';
    const pending = pendingIncoming;
    pendingIncoming = null;
    const current = call;
    call = null;
    if (pending && pending !== current) {
      try { pending.reject(); } catch (_) { /* already gone */ }
    }
    if (current) {
      try { current.disconnect(); } catch (_) { /* already gone */ }
    }
    patch({ status: 'ended', inputLevel: 0, outputLevel: 0 });
  }

  function setMuted(muted) {
    desiredMuted = !!muted;
    if (call && typeof call.mute === 'function') {
      try { call.mute(desiredMuted); } catch (_) { /* call not accepted yet */ }
    }
    patch({ muted: desiredMuted });
  }

  function sendDigits(digits) {
    if (!call || typeof call.sendDigits !== 'function') return;
    const tone = String(digits == null ? '' : digits);
    if (!tone) return;
    call.sendDigits(tone);
  }

  function listDevices() {
    return {
      inputs: state.devices.inputs.slice(),
      outputs: state.devices.outputs.slice(),
      inputId: state.devices.inputId,
      outputId: state.devices.outputId
    };
  }

  async function setInputDevice(id) {
    if (!device || !device.audio) throw new Error('voice_device_not_ready');
    await device.audio.setInputDevice(String(id));
    refreshDeviceLists(String(id), state.devices.outputId);
  }

  async function setOutputDevice(id) {
    if (!device || !device.audio || !device.audio.speakerDevices) throw new Error('voice_device_not_ready');
    await device.audio.speakerDevices.set(String(id));
    refreshDeviceLists(state.devices.inputId, String(id));
  }

  function subscribe(fn) {
    if (listeners.indexOf(fn) === -1) listeners.push(fn);
    try { fn(state); } catch (err) { console.error(err); }
    return function unsubscribe() {
      const idx = listeners.indexOf(fn);
      if (idx !== -1) listeners.splice(idx, 1);
    };
  }

  function getState() {
    return state;
  }

  return {
    register,
    destroy,
    expectCall,
    accept,
    hangup,
    setMuted,
    sendDigits,
    listDevices,
    setInputDevice,
    setOutputDevice,
    subscribe,
    getState
  };
}
